import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getTeam, updateTeam, listTeamUsers, deleteAllTeamUsers, deleteTeamUser, uploadTeamLogo, clearAdminPassword } from '../utils/api';

const FIELDS = [
  { key: 'name', label: 'Team name', type: 'text', required: true },
  { key: 'access_code', label: 'Access code', type: 'text', mono: true, uppercase: true, required: true },
  { key: 'primary_contact_first_name', label: 'Contact first name', type: 'text' },
  { key: 'primary_contact_last_name', label: 'Contact last name', type: 'text' },
  { key: 'primary_contact_email', label: 'Contact email', type: 'email' },
  { key: 'primary_contact_phone', label: 'Contact phone', type: 'text' }
];

export default function TeamDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [teamRes, usersRes] = await Promise.all([getTeam(id), listTeamUsers(id)]);
      setTeam(teamRes.team);
      setDraft(teamRes.team);
      setUsers(usersRes.users);
    } catch (err) {
      if (err.status === 401) {
        clearAdminPassword();
        navigate('/');
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function update(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function getDirtyFields() {
    const dirty = {};
    for (const field of FIELDS) {
      const a = draft[field.key] || '';
      const b = team[field.key] || '';
      if (a !== b) dirty[field.key] = draft[field.key] || '';
    }
    return dirty;
  }

  async function handleSave() {
    const dirty = getDirtyFields();
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateTeam(id, dirty);
      setTeam(result.team);
      setDraft(result.team);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    setDraft(team);
    setSaveError(null);
  }

  function copyAccessCode() {
    if (!team) return;
    navigator.clipboard.writeText(team.access_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (loading) return <Layout title="Team"><div className="card"><p>Loading...</p></div></Layout>;
  if (error) return <Layout title="Team"><div className="card" style={{ borderLeft: '4px solid #d70015' }}><p>{error}</p></div></Layout>;
  if (!team) return null;

  const dirty = getDirtyFields();
  const isDirty = Object.keys(dirty).length > 0;

  async function handleLogoUpload(file) {
    try {
      const result = await uploadTeamLogo(id, file);
      setTeam(prev => ({ ...prev, logo_url: result.logo_url }));
    } catch (err) {
      alert('Failed to upload logo: ' + err.message);
    }
  }

  async function handleDeleteAllUsers() {
    try {
      await deleteAllTeamUsers(id);
      setUsers([]);
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function handleDeleteUser(userId) {
    try {
      await deleteTeamUser(id, userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  return (
    <Layout title={team.name}>
      <style>{css}</style>

      <div className="page-head">
        <div>
          <Link to="/teams" className="back-link">← All teams</Link>
          <h1 className="h1">{team.name}</h1>
        </div>
      </div>

      <div className="card">
        <div className="access-section">
          <div>
            <div className="muted-label">Access Code</div>
            <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:8}}>
            <label style={{cursor:'pointer',width:64,height:64,borderRadius:12,border:'1.5px dashed #c0c0c8',overflow:'hidden',background:'#f5f5f7',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}} title="Upload team logo">
              {team.logo_url
                ? <img src={process.env.REACT_APP_API_URL + team.logo_url} alt="logo" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                : <span style={{fontSize:11,color:'#aeaeb2',textAlign:'center',lineHeight:1.3}}>+ Logo</span>}
              <input type="file" accept="image/*" style={{display:'none'}} onChange={e => e.target.files[0] && handleLogoUpload(e.target.files[0])} />
            </label>
            <code className="access-code-large">{team.access_code}</code>
          </div>
          </div>
          <button onClick={copyAccessCode} className="btn-secondary">
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Team details</h2>
        <div className="form-grid">
          {FIELDS.map((field) => (
            <label key={field.key} className="field">
              <span className="field-label">{field.label}{field.required && ' *'}</span>
              <input
                type={field.type}
                value={draft[field.key] || ''}
                onChange={(e) => update(field.key, field.uppercase ? e.target.value.toUpperCase() : e.target.value)}
                className={field.mono ? 'field-input field-input-mono' : 'field-input'}
              />
            </label>
          ))}
        </div>

        {saveError && <p className="form-error">{saveError}</p>}

        <div className="form-actions">
          {isDirty && (
            <>
              <button onClick={handleCancelEdit} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </>
          )}
          {!isDirty && <p className="muted small">No unsaved changes</p>}
        </div>
      </div>

      <div className="card">
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <h2 className="card-title" style={{margin:0}}>Users on this team ({users.length})</h2>
          {users.length > 0 && <button className="btn-delete-sm" onClick={handleDeleteAllUsers}>Delete All</button>}
        </div>
        {users.length === 0 ? (
          <p className="muted">No users yet. Users are created automatically when buyers check out using the team's access code.</p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td><button className="btn-delete-sm" onClick={() => handleDeleteUser(u.id)}>Delete</button></td>
                      <td className="muted">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="users-mobile">
              {users.map((u) => (
                <div key={u.id} className="user-mobile-card">
                  <div className="user-mobile-name">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</div>
                  <div className="user-mobile-row"><span className="muted">Email</span><span>{u.email}</span></div>
                  {u.phone && <div className="user-mobile-row"><span className="muted">Phone</span><span>{u.phone}</span></div>}
                  <div className="user-mobile-row"><span className="muted">Joined</span><span>{new Date(u.created_at).toLocaleDateString()}</span></div>
                  <button className="btn-delete-sm" style={{marginTop:8}} onClick={() => handleDeleteUser(u.id)}>Delete</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

const css = `
  .page-head { margin-bottom: 20px; }
  .back-link { color: #0071e3; text-decoration: none; font-size: 14px; }
  .h1 { margin: 4px 0 0 0; font-size: 28px; font-weight: 600; color: #1d1d1f; }
  .card {
    background: #fff; border-radius: 20px;
    box-shadow: 0 2px 20px rgba(0,0,0,0.06);
    padding: 24px; margin-bottom: 20px;
  }
  .card-title { margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: #1d1d1f; }
  .muted { color: #86868b; font-size: 14px; margin: 0; }
  .small { font-size: 13px; }
  .muted-label { color: #86868b; font-size: 13px; font-weight: 500; margin-bottom: 6px; }

  .access-section {
    display: flex; justify-content: space-between; align-items: center; gap: 16px;
  }
  .access-code-large {
    font-family: ui-monospace, monospace;
    font-size: 22px; font-weight: 600; color: #1d1d1f;
    background: #f5f5f7; padding: 8px 16px; border-radius: 10px;
    display: inline-block; letter-spacing: 1px;
  }

  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field-label { font-size: 13px; color: #86868b; font-weight: 500; }
  .field-input {
    padding: 10px 14px; border-radius: 10px; border: 1px solid #d2d2d7;
    font-size: 15px; font-family: inherit; outline: none; box-sizing: border-box; width: 100%;
  }
  .field-input:focus { border-color: #0071e3; }
  .field-input-mono { font-family: ui-monospace, monospace; letter-spacing: 0.5px; }
  .form-error { color: #d70015; font-size: 14px; margin: 0 0 12px 0; }
  .form-actions { display: flex; gap: 12px; justify-content: flex-end; align-items: center; }
  .btn-primary {
    padding: 10px 20px; border-radius: 12px; border: none;
    background: #0071e3; color: #fff; font-size: 15px;
    font-weight: 500; font-family: inherit; cursor: pointer;
  }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary {
    padding: 10px 20px; border-radius: 12px;
    background: transparent; border: 1px solid #d2d2d7;
    color: #1d1d1f; font-size: 15px; font-family: inherit; cursor: pointer;
  }

  .table-wrap { width: 100%; overflow-x: auto; }
  .btn-delete-sm { background: none; border: 1.5px solid #e0e0e5; border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 600; color: #c0392b; cursor: pointer; font-family: inherit; transition: all 0.12s; }
  .btn-delete-sm:hover { background: #fce4ec; border-color: #c0392b; }
  .users-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .users-table th {
    text-align: left; padding: 10px 12px; color: #86868b;
    font-weight: 500; font-size: 12px; text-transform: uppercase;
    border-bottom: 1px solid #f0f0f2;
  }
  .users-table td { padding: 12px; color: #1d1d1f; border-bottom: 1px solid #f5f5f7; }
  .users-table tr:last-child td { border-bottom: none; }
  .users-mobile { display: none; }
  .user-mobile-card {
    border: 1px solid #f0f0f2; border-radius: 12px;
    padding: 12px 14px; margin-bottom: 10px;
  }
  .user-mobile-name { font-weight: 500; color: #1d1d1f; margin-bottom: 8px; }
  .user-mobile-row {
    display: flex; justify-content: space-between; padding: 3px 0; font-size: 14px;
  }

  @media (max-width: 700px) {
    .h1 { font-size: 22px; }
    .card { padding: 16px; border-radius: 16px; }
    .access-section { flex-direction: column; align-items: stretch; gap: 12px; }
    .access-code-large { font-size: 18px; text-align: center; }
    .form-grid { grid-template-columns: 1fr; }
    .table-wrap { display: none; }
    .users-mobile { display: block; }
  }
`;
